import { useRef, type MouseEvent, type PointerEvent, type TouchEvent } from 'react';

export interface SafeBackdropDismissOptions {
  /** Minimum movement in pixels to consider a pointer gesture a drag (default: 6) */
  dragThreshold?: number;
}

/**
 * Hook to ensure clicking the backdrop of a modal only dismisses it
 * when it was an intentional, stationary click directly on the backdrop.
 *
 * Prevents accidental modal closing during:
 * - Dragging or scrolling gestures on mobile or touchscreens
 * - Mouse drag operations starting inside the modal card and releasing outside
 * - Swiping while keyboard is visible on mobile
 */
export function useSafeBackdropDismiss(onClose: () => void, options: SafeBackdropDismissOptions = {}) {
  const threshold = options.dragThreshold ?? 6;
  const isPointerDownOnBackdropRef = useRef(false);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const wasDraggedRef = useRef(false);

  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    if (e.target === e.currentTarget) {
      isPointerDownOnBackdropRef.current = true;
      startPosRef.current = { x: e.clientX, y: e.clientY };
      wasDraggedRef.current = false;
    } else {
      isPointerDownOnBackdropRef.current = false;
      startPosRef.current = null;
      wasDraggedRef.current = false;
    }
  };

  const onMouseDown = (e: MouseEvent<HTMLElement>) => {
    if (e.target === e.currentTarget) {
      isPointerDownOnBackdropRef.current = true;
      startPosRef.current = { x: e.clientX, y: e.clientY };
      wasDraggedRef.current = false;
    } else {
      isPointerDownOnBackdropRef.current = false;
      startPosRef.current = null;
      wasDraggedRef.current = false;
    }
  };

  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    if (!startPosRef.current) return;
    const dist = Math.hypot(e.clientX - startPosRef.current.x, e.clientY - startPosRef.current.y);
    if (dist > threshold) {
      wasDraggedRef.current = true;
    }
  };

  const onMouseMove = (e: MouseEvent<HTMLElement>) => {
    if (!startPosRef.current) return;
    const dist = Math.hypot(e.clientX - startPosRef.current.x, e.clientY - startPosRef.current.y);
    if (dist > threshold) {
      wasDraggedRef.current = true;
    }
  };

  const onTouchStart = (e: TouchEvent<HTMLElement>) => {
    if (e.target === e.currentTarget && e.touches[0]) {
      isPointerDownOnBackdropRef.current = true;
      startPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      wasDraggedRef.current = false;
    } else {
      isPointerDownOnBackdropRef.current = false;
      startPosRef.current = null;
      wasDraggedRef.current = false;
    }
  };

  const onTouchMove = (e: TouchEvent<HTMLElement>) => {
    if (!startPosRef.current || !e.touches[0]) return;
    const dist = Math.hypot(e.touches[0].clientX - startPosRef.current.x, e.touches[0].clientY - startPosRef.current.y);
    if (dist > threshold) {
      wasDraggedRef.current = true;
    }
  };

  const onClick = (e: MouseEvent<HTMLElement>) => {
    // 1. Target must be the backdrop itself (not children)
    if (e.target !== e.currentTarget) return;
    // 2. Gesture must have started directly on the backdrop
    if (!isPointerDownOnBackdropRef.current) return;
    // 3. Must not have moved/dragged beyond threshold
    if (wasDraggedRef.current) return;
    if (startPosRef.current) {
      const dist = Math.hypot(e.clientX - startPosRef.current.x, e.clientY - startPosRef.current.y);
      if (dist > threshold) return;
    }

    isPointerDownOnBackdropRef.current = false;
    startPosRef.current = null;
    wasDraggedRef.current = false;
    onClose();
  };

  return {
    onPointerDown,
    onPointerMove,
    onMouseDown,
    onMouseMove,
    onTouchStart,
    onTouchMove,
    onClick,
  };
}
